import { compose } from '@descope-ui/common/utils';
import {
  getComponentName,
  limitAbbreviation,
} from '@descope-ui/common/components-helpers';
import {
  createStyleMixin,
  draggableMixin,
  createProxy,
  componentNameValidationMixin,
  createDynamicDataMixin,
} from '@descope-ui/common/components-mixins';
import { TextClass } from '@descope-ui/descope-text/class';
import { ListClass } from '@descope-ui/descope-list/class';
import { ListItemClass } from '@descope-ui/descope-list-item/class';

export const componentName = getComponentName('apps-list');

const itemRenderer = ({ name, icon, url }, _, ref) => `
  <a ${url ? `href="${url}" title="${url}"` : ''} ${ref.openInSameWindow ? '' : 'target="_blank"'}>
    <descope-list-item>
    <div class="avatar-wrapper">
      <descope-avatar
        ${icon ? `img="${icon}"` : ''}
        ${name ? `display-name="${name}" abbr=${limitAbbreviation(name)}` : ''}
        size="${ref.logoSize || ref.size}"
      ></descope-avatar>
    </div>
    <descope-text
      variant="${ref.itemTextVariant}"
      mode="primary"
    >${name}</descope-text>
    </descope-list-item>
  </a>
`;

const customMixin = (superclass) =>
  class AppsListMixinClass extends superclass {
    // size controls both item logo size and font size
    get size() {
      return this.getAttribute('size') || 'sm';
    }

    get itemTextVariant() {
      return this.getAttribute('item-text-variant') || 'body1';
    }

    get logoSize() {
      return this.getAttribute('logo-size');
    }

    get openInSameWindow() {
      return this.getAttribute('open-in-same-window') === 'true';
    }
  };

export const AppsListClass = compose(
  createStyleMixin({
    mappings: {
      maxHeight: { selector: () => ':host' },
      minHeight: { selector: () => ':host' },
      hostDirection: { selector: () => ':host', property: 'direction' },
      gap: {
        selector: ListClass.componentName,
        property: ListClass.cssVarList.gap,
      },
      maxRowItems: {
        selector: ListClass.componentName,
        property: ListClass.cssVarList.maxRowItems,
      },
      itemsFontWeight: {
        selector: TextClass.componentName,
        property: TextClass.cssVarList.fontWeightOverride,
      },
      itemsFontSize: {
        selector: TextClass.componentName,
        property: TextClass.cssVarList.fontSizeOverride,
      },
      itemsTextAlign: {
        selector: TextClass.componentName,
        property: TextClass.cssVarList.textAlign,
      },
      itemBackgroundColor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.backgroundColor,
      },
      itemHoverBackgroundColor: {
        selector: `${ListItemClass.componentName}:hover`,
        property: ListItemClass.cssVarList.backgroundColor,
      },
      backgroundColor: {
        selector: ListClass.componentName,
        property: ListClass.cssVarList.backgroundColorOverride,
      },
      itemBorderStyle: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.borderStyleOverride,
      },
      itemBorderColor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.borderColorOverride,
      },
      itemBorderWidth: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.borderWidthOverride,
      },
      itemVerticalPadding: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.verticalPaddingOverride,
      },
      itemHorizontalPadding: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.horizontalPaddingOverride,
      },
      itemAlignment: [
        { selector: () => '.avatar-wrapper', property: 'text-align' },
        {
          selector: TextClass.componentName,
          property: TextClass.cssVarList.textAlign,
        },
      ],
    },
  }),
  createDynamicDataMixin({
    itemRenderer,
    rerenderAttrsList: [
      'size',
      'item-text-variant',
      'logo-size',
      'open-in-same-window',
    ],
  }),
  draggableMixin,
  componentNameValidationMixin,
  customMixin,
)(
  createProxy({
    slots: ['empty-state'],
    wrappedEleName: 'descope-list',
    excludeAttrsSync: ['tabindex', 'class', 'empty', 'style'],
    componentName,
    style: () => `
      :host {
        width: 100%;
        display: inline-flex;
      }

      descope-text::part(text-wrapper) {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      descope-list-item[variant="tile"] .avatar-wrapper {
        width: 100%;
      }

      .avatar-wrapper {
        line-height: 0;
      }

      a {
        text-decoration: none;
      }

      descope-text {
        ${TextClass.cssVarList.hostDirection}: var(${AppsListClass.cssVarList.hostDirection});
        max-width: 100%;
        width: 100%;
      }
    `,
  }),
);
